// src/components/InquiriesContacts.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GoogleIcon from "@mui/icons-material/Google"; // Gmail
import WindowIcon from "@mui/icons-material/Window"; // Outlook

// Utils & API
import { trackedUrl, hasValidEmail, hasValidPhone } from "../utils/trackedLinks";
import api from "../axiosConfig";

const MotionRow = motion(TableRow);

/* ---------- helpers ---------- */
function toDate(d) {
  if (!d) return null;
  if (d?.toDate) return d.toDate();
  if (typeof d?.seconds === "number") return new Date(d.seconds * 1000);
  if (d instanceof Date) return d;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const fmt = (d) => {
  const dt = toDate(d);
  if (!dt) return "—";
  return (
    dt.toLocaleDateString() +
    " " +
    dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

function rowsFromInquiries(list) {
  const byEmail = new Map();
  const byPhone = new Map();

  for (const inq of list || []) {
    const name = inq?.formData?.fullName || inq?.customerName || "(Unknown)";
    const email = (inq?.formData?.email || "").trim().toLowerCase();
    const phone = (inq?.formData?.phone || "").trim();
    const type = inq?.type || inq?.inquiryType || inq?.inquiry_type || "inquiry";
    const createdAt = inq?.createdAt ?? inq?.created_at;
    const updatedAt = inq?.updatedAt ?? inq?.updated_at ?? createdAt;

    const key = email || phone;
    if (!key) continue;

    const existing =
      (email ? byEmail.get(email) : null) || (phone ? byPhone.get(phone) : null);

    if (!existing) {
      const rec = {
        id: inq.id, // used for tracking
        name,
        email,
        phone,
        type,
        firstSeen: createdAt,
        lastSeen: updatedAt,
        count: 1,
        _contact_id: inq?.google_contact_id || undefined,
      };
      if (email) byEmail.set(email, rec);
      else byPhone.set(phone, rec);
    } else {
      existing.count += 1;

      const c = toDate(createdAt);
      const u = toDate(updatedAt);

      if (c && (!existing.firstSeen || toDate(existing.firstSeen) > c)) {
        existing.firstSeen = createdAt;
      }
      if (u && (!existing.lastSeen || toDate(existing.lastSeen) < u)) {
        existing.lastSeen = updatedAt;
      }

      if (!existing.email && email) existing.email = email;
      if (!existing.phone && phone) existing.phone = phone;
      if (!existing.type && type) existing.type = type;
    }
  }

  const rows = [...byEmail.values(), ...byPhone.values()];
  rows.sort(
    (a, b) =>
      (toDate(b.lastSeen)?.getTime() || 0) - (toDate(a.lastSeen)?.getTime() || 0)
  );
  return rows;
}

/* ---------- component ---------- */
export default function InquiriesContacts({ inquiriesFeed = [] }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  // EMAIL MENU STATE
  const [emailMenuAnchor, setEmailMenuAnchor] = useState(null);
  const [activeContact, setActiveContact] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadFromApi() {
      try {
        // axios baseURL already ends in /api, so the path must NOT repeat it.
        const res = await api.get("/inquiries");
        const payload = res?.data?.data || res?.data || [];
        if (!cancelled) setRows(rowsFromInquiries(payload));
      } catch (e) {
        console.warn("[InquiriesContacts] API load failed", e?.response || e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (Array.isArray(inquiriesFeed) && inquiriesFeed.length > 0) {
      setRows(rowsFromInquiries(inquiriesFeed));
      setLoading(false);
    } else {
      loadFromApi();
    }

    return () => {
      cancelled = true;
    };
  }, [inquiriesFeed]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(s) ||
        (r.email || "").toLowerCase().includes(s) ||
        (r.phone || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  // EMAIL MENU HANDLERS
  const handleEmailClick = (event, row) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveContact(row);
    setEmailMenuAnchor(event.currentTarget);
  };

  const handleEmailMenuClose = () => {
    setEmailMenuAnchor(null);
    setActiveContact(null);
  };

  // IMPORTANT: open tracked link in a NEW TAB using window.open
  // This avoids the "mailto does nothing" problem and avoids silent blocked navigation in some browsers.
  const openTracked = useCallback((url) => {
    if (!url) return;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    // Fallback: if popup blocked, navigate current tab
    if (!win) window.location.href = url;
  }, []);

  const handleEmailSelect = (provider) => {
    if (!activeContact || !hasValidEmail(activeContact.email)) return;

    const customerName = activeContact.name || "Customer";
    const serviceType = activeContact.type || "Inquiry";

    const subjectLine = `Regarding: ${serviceType}`;
    const bodyContent =
      `Hi ${customerName},\n\n` +
      `Thank you for your inquiry regarding ${serviceType}.\n\n` +
      `How can we help you further?\n\n` +
      `Best regards,\nAbisiniya Team`;

    // Use google_contact_id if available, else inquiry id, else email
    const contactId =
      activeContact._contact_id || activeContact.id || activeContact.email;

    // provider must match backend buildTarget(): gmail|outlook|yahoo|email
    const link = trackedUrl(provider, {
      to: activeContact.email,
      contact_id: contactId,
      contact_name: customerName,
      source: "inquiries_contacts",
      subject: subjectLine,
      body: bodyContent,
    });

    openTracked(link);
    handleEmailMenuClose();
  };

  return (
    <motion.div key="inq-contacts" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={800}>
            Inquiries Contacts ({filtered.length})
          </Typography>

          <TextField
            size="small"
            placeholder="Filter name / email / phone"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((r, i) => {
                  const canEmail = hasValidEmail(r.email);
                  const canPhone = hasValidPhone(r.phone);

                  const contactId =
                    r._contact_id || r.id || r.email || r.phone || `row-${i}`;

                  // tracked links for tel/whatsapp
                  const telHref = canPhone
                    ? trackedUrl("tel", {
                        to: r.phone,
                        contact_id: contactId,
                        source: "inquiries_contacts",
                        contact_name: r.name || "(Unknown)",
                      })
                    : undefined;

                  const waHref = canPhone
                    ? trackedUrl("whatsapp", {
                        to: r.phone,
                        contact_id: contactId,
                        source: "inquiries_contacts",
                        contact_name: r.name || "(Unknown)",
                        text: "Hello! Can we chat about your inquiry?",
                      })
                    : undefined;

                  return (
                    <MotionRow
                      key={`${r.email || r.phone || i}`}
                      hover
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      sx={{
                        position: "relative",
                        "&:hover .inq-actions": {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {r.name || "(Unknown)"}
                          </Typography>
                          {r._contact_id && <Chip size="small" label="Linked" />}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography noWrap>{r.email || "—"}</Typography>
                      </TableCell>

                      <TableCell sx={{ position: "relative" }}>
                        {r.phone || "—"}

                        {/* Hover capsule */}
                        <Box
                          className="inq-actions"
                          sx={{
                            position: "relative",
                            left: -300,
                            mt: -2.5,
                            bgcolor: "success.main",
                            color: "#fff",
                            borderRadius: 999,
                            px: 1,
                            py: 0.25,
                            display: "inline-flex",
                            gap: 0.5,
                            boxShadow: 2,
                            opacity: 0,
                            transform: "translateY(4px)",
                            transition: "all 160ms ease",
                          }}
                        >
                          {/* PHONE */}
                          <Tooltip title={canPhone ? "Call" : "No phone"}>
                            <span>
                              <IconButton
                                component="a"
                                href={telHref}
                                size="small"
                                disabled={!canPhone}
                                // do not open new tab for tel
                                sx={{
                                  color: "#fff",
                                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                }}
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* WHATSAPP */}
                          <Tooltip title={canPhone ? "WhatsApp" : "No phone"}>
                            <span>
                              <IconButton
                                component="a"
                                href={waHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                disabled={!canPhone}
                                sx={{
                                  color: "#fff",
                                  opacity: canPhone ? 1 : 0.35,
                                  filter: canPhone ? "none" : "grayscale(100%)",
                                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                }}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* EMAIL (menu) */}
                          <Tooltip title={canEmail ? "Email Options" : "No email"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!canEmail}
                                onClick={(e) => handleEmailClick(e, r)}
                                sx={{
                                  color: "#fff",
                                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                }}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="More">
                            <span>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>{r.type || "—"}</TableCell>
                      <TableCell>{fmt(r.firstSeen)}</TableCell>
                      <TableCell>{fmt(r.lastSeen)}</TableCell>

                      <TableCell align="right">
                        <Chip size="small" label={r.count || 1} sx={{ bgcolor: "success.light" }} />
                      </TableCell>
                    </MotionRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Email Options Menu */}
        <Menu
          anchorEl={emailMenuAnchor}
          open={Boolean(emailMenuAnchor)}
          onClose={handleEmailMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            elevation: 3,
            sx: { borderRadius: 2, mt: 1, minWidth: 180 },
          }}
        >
          <MenuItem onClick={() => handleEmailSelect("gmail")}>
            <ListItemIcon>
              <GoogleIcon fontSize="small" sx={{ color: "#DB4437" }} />
            </ListItemIcon>
            <ListItemText>Gmail</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleEmailSelect("outlook")}>
            <ListItemIcon>
              <WindowIcon fontSize="small" sx={{ color: "#0078D4" }} />
            </ListItemIcon>
            <ListItemText>Outlook Web</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleEmailSelect("yahoo")}>
            <ListItemIcon>
              <EmailIcon fontSize="small" sx={{ color: "#6001D2" }} />
            </ListItemIcon>
            <ListItemText>Yahoo Mail</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleEmailSelect("email")}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Default App (mailto)</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>
    </motion.div>
  );
}
